import React, { forwardRef } from "react";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import moment from "moment";

const PrintablePreOrder = forwardRef(({ preorder, supplier, items, logoUrl }, ref) => {
  const { pharmacyPOS } = usePharmacy();
  const fallbackSrc = `${process.env.PUBLIC_URL}/logo.png`;

  // Assuming pharmacyPOS has address or other details; if not, you can add them
  const pharmacyName = pharmacyPOS?.facility_name || 'CLINICDOTS PHARMACY';
  const pharmacyAddress = pharmacyPOS?.address || pharmacyPOS?.contact_person?.address || 'N/A'; // Placeholder; fetch if available
  const pharmacyPhone = pharmacyPOS?.phone || pharmacyPOS?.contact_person?.phone_code + pharmacyPOS?.contact_person?.mobile || 'N/A'; // Placeholder

  const supplierName = supplier?.companyName || 'N/A';
  const supplierContact = supplier?.contactPerson || 'N/A';
  const supplierPhone = supplier?.countryCode + supplier?.phone || 'N/A'; // Assuming available in supplier
  const supplierAddress = supplier?.address || 'N/A'; // Assuming available

  const orderDate = moment(preorder?.createdAt).format("DD MMM YYYY");
  const orderId = preorder?._id || 'N/A';

  // Calculate total quantity
  const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);

  return (
    <div ref={ref} style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        {/* <S3ImageLoader
          className="m-0"
          fileName={pharmacyPOS?.logo}
          style={{ height: '160px', width: '160px', objectFit: 'cover', borderRadius: '100%' }}
          alt="profile"
          fallbackSrc={fallbackSrc}
        /> */}
        <img
          src={logoUrl || fallbackSrc}
          alt="logo"
          style={styles.logo}
          crossOrigin="anonymous"
          onError={(e) => { e.target.src = fallbackSrc; }}
        />
        <h2 style={styles.title}>ORDER</h2>
        <div style={styles.subTitle}>{pharmacyName}</div>
        <div style={styles.address}>{pharmacyAddress} | Phone: {pharmacyPhone}</div>
      </div>

      {/* Order Info */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Order Details</h3>
        <table style={styles.infoTable}>
          <tbody>
            <tr>
              <td style={styles.infoLabel}>Order ID:</td>
              <td style={styles.infoValue}>{orderId}</td>
            </tr>
            <tr>
              <td style={styles.infoLabel}>Date:</td>
              <td style={styles.infoValue}>{orderDate}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Supplier Info - Two Column Layout */}
      <div style={styles.section}>
        <table style={styles.twoColumnTable}>
          <tbody>
            <tr>
              <td style={styles.column}>
                <h3 style={styles.sectionTitle}>From:</h3>
                <div style={styles.addressBlock}>
                  <strong>{pharmacyName}</strong><br />
                  {pharmacyAddress}<br />
                  Phone: {pharmacyPhone}
                </div>
              </td>
              <td style={styles.column}>
                <h3 style={styles.sectionTitle}>To (Supplier):</h3>
                <div style={styles.addressBlock}>
                  <strong>{supplierName}</strong><br />
                  Contact: {supplierContact}<br />
                  Phone: {supplierPhone}<br />
                  Address: {supplierAddress}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Items Table */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Items</h3>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '5%' }}>#</th>
              <th style={{ ...styles.th, width: '50%' }}>Product</th>
              <th style={{ ...styles.th, width: '15%' }}>Form</th>
              <th style={{ ...styles.th, width: '15%' }}>Package</th>
              <th style={{ ...styles.th, width: '15%' }}>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i}>
                <td style={styles.tdCenter}>{i + 1}</td>
                <td style={styles.td}>
                  <strong>{item.brandName}</strong>
                  <div style={styles.muted}>{item.saltName}</div>
                </td>
                <td style={styles.tdCenter}>{item.productForm}</td>
                <td style={styles.tdCenter}>{item.package}</td>
                <td style={styles.tdCenter}>{item.quantity}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="4" style={{ ...styles.tdRight, fontWeight: 'bold' }}>Total Quantity:</td>
              <td style={styles.tdCenter}>{totalQuantity}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <p>Thank you for your business. Please process this pre-order at your earliest convenience.</p>
        <div style={styles.signature}>
          <div>Authorized Signature: ______________________________</div>
          <div>Date: ______________________________</div>
        </div>
        <div style={styles.pageNumber}>Page 1 of 1</div> {/* Adjust if multi-page */}
      </div>
    </div>
  );
});

export default PrintablePreOrder;

const styles = {
  page: {
    padding: "30px",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontSize: "12px",
    color: "#333",
    maxWidth: "800px",
    margin: "0 auto",
    backgroundColor: "#fff",
  },
  header: {
    textAlign: "center",
    marginBottom: "30px",
    borderBottom: "2px solid #1B255B",
    paddingBottom: "15px",
  },
  logo: {
    height: "60px",
    marginBottom: "10px",
  },
  title: {
    margin: "0 0 5px 0",
    fontSize: "24px",
    color: "#1B255B",
    textTransform: "uppercase",
  },
  subTitle: {
    fontSize: "14px",
    fontWeight: "bold",
    marginBottom: "5px",
  },
  address: {
    fontSize: "11px",
    color: "#666",
  },
  section: {
    marginBottom: "25px",
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: "bold",
    color: "#1B255B",
    marginBottom: "10px",
    textTransform: "uppercase",
    borderBottom: "1px solid #ddd",
    paddingBottom: "5px",
  },
  infoTable: {
    width: "100%",
    borderCollapse: "collapse",
  },
  infoLabel: {
    width: "30%",
    padding: "5px",
    fontWeight: "bold",
    borderBottom: "1px solid #eee",
  },
  infoValue: {
    padding: "5px",
    borderBottom: "1px solid #eee",
    letterSpacing: "1px",
  },
  twoColumnTable: {
    width: "100%",
    borderCollapse: "collapse",
  },
  column: {
    width: "50%",
    verticalAlign: "top",
    padding: "10px",
  },
  addressBlock: {
    fontSize: "12px",
    lineHeight: "1.4",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "10px",
  },
  th: {
    border: "1px solid #ccc",
    padding: "10px",
    backgroundColor: "#1B255B",
    color: "#fff",
    fontSize: "12px",
    textAlign: "left",
    textTransform: "uppercase",
  },
  td: {
    border: "1px solid #ccc",
    padding: "10px",
    verticalAlign: "top",
    whiteSpace: "normal", // Allow wrapping
    wordBreak: "break-word", // Break long words
  },
  tdCenter: {
    border: "1px solid #ccc",
    padding: "10px",
    textAlign: "center",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  tdRight: {
    border: "1px solid #ccc",
    padding: "10px",
    textAlign: "right",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  muted: {
    fontSize: "11px",
    color: "#666",
    marginTop: "3px",
  },
  footer: {
    marginTop: "40px",
    textAlign: "center",
    fontSize: "11px",
    color: "#666",
    borderTop: "1px solid #ddd",
    paddingTop: "15px",
  },
  signature: {
    marginTop: "20px",
    textAlign: "left",
    fontSize: "12px",
  },
  pageNumber: {
    marginTop: "20px",
    fontSize: "10px",
    color: "#999",
  },
};